import React from "react";
import { enqueueSnackbar } from "notistack";
import { Modal, Tooltip, Button, Segmented, Tag } from "antd";
import { malimaxOneById } from "../../../../libs/apis";
import { MalimaxOneI } from "../../../../libs/interfaces";
import { Box } from "@mui/material";
import type { Dayjs } from "dayjs";
import {
  CloseOutlined,
  FundViewOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  VideoCameraAddOutlined,
} from "@ant-design/icons";
import { OneCard, OneVideo } from "../../../../libs/shared";
import "./show-container.css";

interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
  type: "external" | "internal" | "exit" | "video" | "additional";
}

interface ShowContainerPropsI {
  id: number;
  isError?: boolean;
}

export const ShowContainer = ({ id, isError }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) setOpen(true);
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };

  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer
          open={open}
          onClose={handleClose}
          id={id}
          isError={isError}
        />
      )}
    </>
  );
};

/* ─────────────────────────────────────────────── */
/*  Helper: single info field                      */
/* ─────────────────────────────────────────────── */
const Field = ({
  label,
  value,
  full,
}: {
  label: string;
  value?: string | null;
  full?: boolean;
}) => (
  <div className={`info-block${full ? " full-width" : ""}`}>
    <div className="label">{label}</div>
    <div className={`value${!value ? " empty" : ""}`}>{value || "—"}</div>
  </div>
);

/* ─────────────────────────────────────────────── */
/*  Form info panel                                */
/* ─────────────────────────────────────────────── */
const FormPanel = ({ data }: { data: MalimaxOneI }) => (
  <div className="info-grid">
    {/* Tiempos */}
    <div className="time-row">
      <div className="time-badge">
        <span className="t-label">Hora inicio</span>
        <span className="t-value">{data.hourInit || "—"}</span>
      </div>
      <div className="time-badge">
        <span className="t-label">Hora fin</span>
        <span className="t-value">{data.hourEnd || "—"}</span>
      </div>
      <div className="time-badge">
        <span className="t-label">Guardado</span>
        <span className="t-value">{data.hourSave ? data.hourSave : "—"}</span>
      </div>
    </div>

    {/* General */}
    <div className="form-section-title">General</div>
    <Field label="Cliente" value={data.client} />
    <Field label="ID Cliente" value={data.clientIdentification} />
    <Field label="Tipo Servicio" value={data.typeService} />
    <Field label="Tipo Contenedor" value={data.typeContainer} />
    <Field label="Fecha" value={data.date} />
    <Field label="Ciudad" value={data.city} />
    <Field label="Puerto de entrada" value={data.entryPort} />
    <Field label="Lugar de trabajo" value={data.workplace} />
    <Field label="Dirección" value={data.address} />
    <Field label="Almacén" value={data.storageName} />
    <Field label="Coordenadas" value={data.coordinates} />

    {/* Transporte */}
    <div className="form-section-title">Transporte</div>
    <Field label="Empresa transporte" value={data.companyTransport} />
    <Field label="Placa vehículo" value={data.plateVehicle} />
    <Field label="Conductor" value={data.driverName} />
    <Field label="CI Conductor" value={data.driverIdentification} />

    {/* Personas */}
    <div className="form-section-title">Personas</div>
    <Field label="Inspeccionado por" value={data.inspectedBy} />
    <Field label="Supervisor Exportador" value={data.exporterSupervisor} />
    <Field
      label="ID Supervisor Exportador"
      value={data.exporterSupervisorIdentification}
    />
    <Field label="Asociado" value={data.associated} />
    <Field label="ID Asociado" value={data.associatedIdentification} />
    <Field label="Otros" value={data.others} />
    <Field label="ID Otros" value={data.othersIdentification} />

    {/* Observaciones */}
    {data.observation && (
      <>
        <div className="form-section-title">Observaciones</div>
        <Field label="Observación" value={data.observation} full />
      </>
    )}
  </div>
);

/* ─────────────────────────────────────────────── */
/*  Photos panel (original layout)                 */
/* ─────────────────────────────────────────────── */
const PhotosPanel = ({
  cards,
  totalPhotos,
}: {
  cards: OneCardI[];
  totalPhotos: string[];
}) => (
  <Box>
    <div className="section-header">
      <h3>Fotos Externas</h3>
    </div>
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      }}
    >
      {cards
        .filter((el) => el.type === "external")
        .map((el) => (
          <OneCard data={el} key={`${el.title}-card`} lasFotos={totalPhotos} />
        ))}
    </Box>

    <div className="section-header">
      <h3>Fotos Internas</h3>
    </div>
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      }}
    >
      {cards
        .filter((el) => el.type === "internal")
        .map((el) => (
          <OneCard data={el} key={`${el.title}-card`} lasFotos={totalPhotos} />
        ))}
    </Box>

    <div className="section-header">
      <h3>Fotos Rumbo a finca</h3>
    </div>
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: {
          xs: "0.8fr",
          sm: "1fr 1fr 1fr",
          md: "1fr 1fr 1fr",
          lg: "1fr 1fr 1fr 1fr",
        },
      }}
    >
      {cards
        .filter((el) => el.type === "exit")
        .map((el) => (
          <OneCard data={el} key={`${el.title}-card`} lasFotos={totalPhotos} />
        ))}
    </Box>

    {cards.some((el) => el.type === "additional") && (
      <>
        <div className="section-header">
          <h3>Fotos Adicionales</h3>
        </div>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          }}
        >
          {cards
            .filter((el) => el.type === "additional")
            .map((el) => (
              <OneCard
                data={el}
                key={`${el.title}-card`}
                lasFotos={totalPhotos}
              />
            ))}
        </Box>
      </>
    )}
  </Box>
);

/* ─────────────────────────────────────────────── */
/*  Modal                                          */
/* ─────────────────────────────────────────────── */
interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
  isError?: boolean;
}

export const ModalContainer = ({
  open,
  onClose,
  id,
  isError,
}: ModalContainerPropsI) => {
  const [isLoading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<MalimaxOneI>({
    container: "",
  } as MalimaxOneI);
  const [cards, setCards] = React.useState<OneCardI[]>([]);
  const [totalPhotos, setTotalPhotos] = React.useState<string[]>([]);
  const [tab, setTab] = React.useState<string>("form");

  React.useEffect(() => {
    handleGetData();
    return () => {
      setCards([]);
      setData({ container: "" } as MalimaxOneI);
      setLoading(true);
      setTab("form");
    };
  }, []);

  const handleGetData = async () => {
    try {
      const { data: dataContainerGet } = await malimaxOneById(id);
      if (!dataContainerGet) return;
      const dataContainer = dataContainerGet;
      setData(dataContainerGet);

      const lasFotos: string[] = [];
      const arrCards: any[] = [];

      // External photos
      const externalPhotos = [
        { title: "Foto EIR", src: dataContainer.emptyEirPhoto, alt: "EIR" },
        {
          title: "Foto Dcto. Inspección Prev.",
          src: dataContainer.emptyPreviousInspectionDocumentPhoto,
          alt: "Documento Inspección Previa",
        },
        {
          title: "Foto Placa",
          src: dataContainer.emptyPlatePhoto,
          alt: "Placa",
        },
        {
          title: "Foto CI Conductor",
          src: dataContainer.emptyDriverIdentificationPhoto,
          alt: "Identificación del Conductor",
        },
        {
          title: "Foto Panorámica",
          src: dataContainer.emptyPanoramicPhoto,
          alt: "Panorámica",
          comment: dataContainer.emptyPanoramicComment,
        },
        {
          title: "Foto Sello Adicional",
          src: dataContainer.emptyAditionalStampPhoto,
          alt: "Sello Adicional",
          comment: dataContainer.emptyAditionalStampComment,
        },
        {
          title: "Foto Sello Naviera",
          src: dataContainer.emptyStampNavieraPhoto,
          alt: "Sello Naviera",
          comment: dataContainer.emptyStampNavieraComment,
        },
        {
          title: "Foto Otro Sello",
          src: dataContainer.emptyOtherStampPhoto,
          alt: "Otro Sello",
          comment: dataContainer.emptyOtherStampComment,
        },
        {
          title: "Foto Candado Satelital",
          src: dataContainer.emptySatelliteLockPhoto,
          alt: "Candado Satelital",
          comment: dataContainer.emptySatelliteLockComment,
        },
        {
          title: "Foto Sello Candado Gps",
          src: dataContainer.emptySatelliteLockStampPhoto,
          alt: "Sello Candado Satelital",
          comment: dataContainer.emptySatelliteLockStampComment,
        },
        {
          title: "Foto Lado Derecho",
          src: dataContainer.emptySideRightPhoto,
          alt: "Lado Derecho",
          comment: "",
        },
        {
          title: "Foto Lado Izquierdo",
          src: dataContainer.emptySideLeftPhoto,
          alt: "Lado Izquierdo",
          comment: "",
        },
        {
          title: "Foto Techo Interno",
          src: dataContainer.emptySideUpPhoto,
          alt: "Techo Interno",
          comment: "",
        },
        {
          title: "Foto Piso Interno",
          src: dataContainer.emptySideDownPhoto,
          alt: "Piso Interno",
          comment: "",
        },
        {
          title: "Foto Frontal",
          src: dataContainer.emptyFrontPhoto,
          alt: "Frontal",
          comment: "",
        },
        {
          title: "Foto Posterior",
          src: dataContainer.emptyRearPhoto,
          alt: "Posterior",
          comment: "",
        },
      ];

      externalPhotos.forEach((photo) => {
        arrCards.push({
          title: photo.title,
          noLine: true,
          photos: [{ src: photo.src, alt: photo.alt, showAlt: false }],
          value: (photo as any).comment,
          type: "external",
        });
        lasFotos.push(photo.src as string);
      });

      // Internal photos
      type InternalPhotoKeys = `emptyInternalPhoto${1 | 2 | 3 | 4 | 5 | 6}`;
      type InternalCommentKeys = `emptyInternalComment${1 | 2 | 3 | 4 | 5 | 6}`;

      const internalPhotos = [
        {
          title: "Foto Piso",
          src: dataContainer.emptyFloorPhoto,
          alt: "Piso",
          comment: dataContainer.emptyFloorComment,
        },
        {
          title: "Foto Techo",
          src: dataContainer.emptyRoofPhoto,
          alt: "Techo",
          comment: dataContainer.emptyRoofComment,
        },
        {
          title: "Foto Cubierta del Espejo",
          src: dataContainer.emptyMirrorCoverPhoto,
          alt: "Cubierta del Espejo",
          comment: dataContainer.emptyMirrorCoverComment,
        },
      ];

      for (let i = 1; i <= 6; i++) {
        const photoKey = `emptyInternalPhoto${i}` as InternalPhotoKeys;
        const commentKey = `emptyInternalComment${i}` as InternalCommentKeys;
        internalPhotos.push({
          title: `Foto Interna ${i}`,
          src: dataContainer[photoKey],
          alt: `Interna ${i}`,
          comment: dataContainer[commentKey],
        });
        lasFotos.push(dataContainer[photoKey]);
      }

      internalPhotos.forEach((photo) => {
        arrCards.push({
          title: photo.title,
          noLine: true,
          photos: [{ src: photo.src, alt: photo.alt, showAlt: false }],
          value: photo.comment,
          type: "internal",
        });
        lasFotos.push(photo.src as string);
      });

      // Exit photos
      const exitPhotos = [
        {
          title: "Foto Panorámica",
          src: dataContainer.exitPanoramicPhoto,
          alt: "Panorámica",
          comment: dataContainer.exitPanoramicComment,
        },
        {
          title: "Foto Sellado Temporal",
          src: dataContainer.exitTemporarySealingPhoto,
          alt: "SelladoTemporal",
          comment: dataContainer.exitTemporarySealingComment,
        },
      ];

      exitPhotos.forEach((photo) => {
        arrCards.push({
          title: photo.title,
          noLine: true,
          photos: [{ src: photo.src, alt: photo.alt, showAlt: false }],
          value: photo.comment,
          type: "exit",
        });
        lasFotos.push(photo.src as string);
      });

      (dataContainer.images ?? []).forEach((img, i) => {
        if (!img?.src) return;
        arrCards.push({
          title: `Foto Adicional ${i + 1}`,
          noLine: true,
          photos: [{ src: img.src, alt: img.comment ?? "", showAlt: false }],
          value: img.comment ?? "",
          type: "additional",
        });
        lasFotos.push(img.src as string);
      });

      setTotalPhotos(lasFotos);
      setCards(arrCards);
      setLoading(false);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
      setLoading(false);
    }
  };

  const photoCount = cards.filter((c) => c.photos?.[0]?.src).length;

  return (
    <Modal
      title={
        <p className="modal-header-gradient">
          {`CONTENEDOR N° ${data.container}`}
          {data.statusWorkflow && (
            <Tag
              color={data.statusWorkflowColor || "#25D228"}
              style={{
                marginLeft: 10,
                fontSize: "0.75rem",
                verticalAlign: "middle",
              }}
            >
              {data.statusWorkflow}
            </Tag>
          )}
        </p>
      }
      closeIcon={
        <CloseOutlined
          style={{
            background: "red",
            color: "white",
            padding: "5px",
            borderRadius: "5px",
          }}
        />
      }
      footer={null}
      loading={isLoading}
      open={open}
      onClose={onClose}
      onCancel={onClose}
      width={820}
      centered
      zIndex={1602}
      destroyOnClose
      style={{ padding: 0 }}
    >
      {/* Segmented control */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5, mt: 0.5 }}>
        <Segmented
          className="modal-segmented"
          value={tab}
          onChange={(v) => setTab(v as string)}
          options={[
            {
              label: (
                <span>
                  <InfoCircleOutlined style={{ marginRight: 5 }} />
                  Información
                </span>
              ),
              value: "form",
            },
            {
              label: (
                <span>
                  <PictureOutlined style={{ marginRight: 5 }} />
                  Fotos
                  {photoCount > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        background: "#25D228",
                        color: "white",
                        borderRadius: 10,
                        padding: "0 6px",
                        fontSize: "0.7rem",
                      }}
                    >
                      {photoCount}
                    </span>
                  )}
                </span>
              ),
              value: "photos",
            },
            {
              label: (
                <span>
                  <VideoCameraAddOutlined style={{ marginRight: 5 }} />
                  Video
                </span>
              ),
              value: "video",
            },
          ]}
          style={{ fontWeight: 600 }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ overflowY: "auto", maxHeight: 520, p: 1.5 }}>
        {tab === "form" ? (
          <FormPanel data={data} />
        ) : tab === "photos" ? (
          <PhotosPanel cards={cards} totalPhotos={totalPhotos} />
        ) : tab === "video" ? (
          <div
            style={{
              display: "flex",
              gap: 10,
              minHeight: 490,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <OneVideo src={data.exitDoorVideo} alt="Video Cont. Vacio" />
          </div>
        ) : null}
      </Box>
    </Modal>
  );
};
